import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, TextField, useTheme } from '@mui/material'
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { handlePromise } from '../../../../utils/handlePromisse';
import { Patient as PatientModel , getPatientsByNameObservation } from "../../../../services/patient.service";
import AppModal from "../../../../components/ui/AppModal";

export interface IPatientModalValues {
    id?: number,
    patientName: string,
    observations?: string
}

interface PatientProps {
    open: boolean
    onClose: (values?: IPatientModalValues) => void
    reloadPatients: boolean
    patientSelected: (ids: number[]) => void
}

const isValidState = (state: Partial<IPatientModalValues>) => !!state.patientName && state.patientName.length > 3

const handleSave = (state: Partial<IPatientModalValues>, callback: Function) => {
    if (isValidState(state)) {
        callback(state)
    }
}

const findExistingPatient = (patients: PatientModel[], name?: string) => {
    if (!name) return undefined
    const normalized = name.trim().toLowerCase()
    return patients.find((p) => (p.name || '').trim().toLowerCase() === normalized)
}

export const AddPatient: React.FC<PatientProps> = ({ open, onClose, reloadPatients, patientSelected }) => {
    const theme = useTheme()
    const [state, setState] = useState<Partial<IPatientModalValues>>({})
    const [disabled, setDisabled] = useState(true)
    const [patients, setPatients] = useState<PatientModel[]>([])
    const [selected, setSelected] = useState<PatientModel | null>(null)
    const [observationState, setObservationState] = useState(false)

    const filter = createFilterOptions<PatientModel>();

    const fetchPatients = useCallback(async () => {
        const [result, error] = await handlePromise(getPatientsByNameObservation())
        const fetchedPatients = (Array.isArray(result.data) ? result.data : result.data.data) as PatientModel[];
        const sortedPatients = fetchedPatients.sort((a, b) => {
            if (!a.name || !b.name) return 0;
            return a.name.localeCompare(b.name);
        });
        setPatients(sortedPatients);
    }, [])

    useEffect(() => {
        fetchPatients()
    }, [fetchPatients, reloadPatients])

    useEffect(() => {
        setDisabled(!isValidState(state))
    }, [state])

    useEffect(() => {
        if (!open) {
            setState({})
            setSelected(null)
            setObservationState(false)
        }
    }, [open])

    const autoCompleteStyle = {
        width: "100%",
    };

    return (
        <AppModal
            open={open}
            onClose={() => onClose()}
            kicker="Paciente"
            title="Cadastro de paciente"
            maxWidth={520}
            footer={
                <>
                    <Button
                        onClick={() => onClose()}
                        sx={{ height: 46, px: 3, color: "secondary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.divider}` }}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={disabled}
                        onClick={() => {
                            const existing = state.id ? undefined : findExistingPatient(patients, state.patientName)
                            const resolvedState = existing
                                ? { ...state, id: existing.id, observations: state.observations || existing.observation }
                                : state
                            handleSave(resolvedState as IPatientModalValues, onClose)
                        }}
                        sx={{ height: 46, px: 3 }}
                    >
                        Salvar
                    </Button>
                </>
            }
        >
            <Box>
                <Autocomplete
                    freeSolo
                    options={patients}
                    style={autoCompleteStyle}
                    limitTags={1}
                    clearOnBlur={false}
                    getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                          return option;
                        }
                        if (option.name) {
                          return option.name;
                        }
                        return option.name;
                      }}
                    value={selected}
                    inputValue={state.patientName ?? ''}
                    onChange={(event, newValue) => {
                         if (typeof newValue === 'object' && newValue!== null && newValue.name) {
                            setSelected(newValue)
                            setObservationState(true)
                            setState({
                                id: newValue.id,
                                patientName: newValue.name,
                                observations: newValue.observation ?? ''
                            });
                        } else if (typeof newValue === 'string') {
                            setSelected(null)
                            setObservationState(false)
                            setState({
                                patientName: newValue,
                                observations: ''
                            })
                        } else if (typeof newValue === 'object' && newValue!== null) {
                            setSelected(null)
                            setObservationState(false)
                            setState({ patientName: newValue?.name, observations: '' });
                        } else {
                            setSelected(null)
                            setObservationState(false)
                            setState({})
                        }
                      }}
                    onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'input') {
                            setState((prev) => ({ ...prev, patientName: newInputValue }))
                        }
                      }}
                    filterOptions={(options, params) => {
                        const filtered = filter(options, params)
                        return filtered;
                      }}
                    renderOption={(props, option)=> {
                        return(
                            <li {...props} key={option.id}>
                                {option.name ? option.name : ''}
                            </li>
                        )
                    }}
                    renderInput={(params) => 
                        <TextField
                            {...params}
                            fullWidth
                            id="patientName"
                            color="primary"
                            variant="outlined"
                            label="Nome completo"
                            sx={{ mt: "4px" }}
                            error={!!state.patientName && state.patientName.trim().length < 4}
                            helperText={!!state.patientName && state.patientName.trim().length < 4 ? "O nome deve ter pelo menos 4 caracteres" : undefined}
                        />}
                />

                <TextField
                    fullWidth
                    focused={observationState}
                    id="observations"
                    color="primary"
                    multiline
                    rows={5}
                    variant="outlined"
                    label="Observações"
                    value={state.observations ?? ''}
                    sx={{ marginTop: "16px" }}
                    onChange={(e) => setState({ ...state, observations: e.target.value })}
                />
            </Box>
        </AppModal>
    )
}
